import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, Form, Row, Spinner } from 'react-bootstrap'
import {
  CLASS_BY_STUDY, DEFAULTS, guessGender, splitNamePrefix, undidNamePrefix,
  isThaiDate, thaiDateInput, pathsFor, sectionsFor, formToPayload,
} from './fields'
import { checkIdCard, fetchDormitories } from './api/admissionApi'

// ฟอร์มรับสมัครตัวเดียวที่ใช้ได้ทุกระดับชั้นและทั้งตอนสมัครใหม่/แก้ไข
// เรนเดอร์จากผังใน fields.js ล้วนๆ — เพิ่มช่องกรอกทำที่ผังที่เดียว ไม่ต้องแตะไฟล์นี้

const emptyForm = () =>
  pathsFor('junior').concat(pathsFor('anubal')).reduce((acc, path) => {
    const [group, key] = path.split('.')
    if (!acc[group]) acc[group] = {}
    acc[group][key] = DEFAULTS[path] || ''
    return acc
  }, {})

const gridClass = (col) => `col-12 col-md-${Math.min(col || 6, 12)}`

// ค่าเดิมในระเบียนที่ไม่ตรงกับตัวเลือกไหนเลย ต้องยังเห็นว่ามีค่าอยู่
// ไม่ใช่แสดงเป็นช่องว่างเปล่าจนคนกรอกนึกว่ายังไม่ได้กรอก แล้วเลือกทับของเดิมไป
//
// ของจริงมีเยอะ — st_blood มีทั้ง '-' (2,109 แถว), 'o' ตัวเล็ก, 'โอ', 'บี', 'B+'
// และชั้นที่จบที่เพิ่งเปลี่ยนจากช่องพิมพ์มาเป็นตัวเลือกก็อาจมีค่านอกรายการแบบเดียวกัน
const orphan = (value, known, label) =>
  (value && !known.includes(value)
    ? <option value={value}>{label} (ไม่มีในรายการ)</option>
    : null)

export default function ApplicantForm({
  initialValue,
  prefill,                // ข้อมูลเดิมของนักเรียนเก่า (ดู prefillFromStudent)
  mode = 'create',        // 'create' | 'edit'
  submitting = false,
  onSubmit,
  onCancel,
}) {
  const [form, setForm] = useState(() => initialValue || emptyForm())
  const [errors, setErrors] = useState({})
  const [idCardNote, setIdCardNote] = useState(null)
  const [checkingIdCard, setCheckingIdCard] = useState(false)

  // ช่องชื่อที่ผู้ใช้บอกแล้วว่าเว้นวรรคให้ผิด — เลิกยุ่งกับช่องนั้นไปจนกว่าจะออกจากหน้า
  const manualNames = useRef(new Set())

  // ช่องที่ยังเป็นค่าตั้งต้นที่ระบบใส่ให้ ไม่ใช่ค่าที่คนกรอกตั้งใจใส่
  // ข้อมูลจริงของนักเรียนเก่าต้องทับค่าพวกนี้ได้ ไม่งั้นคนสัญชาติมาเลเซียจะกลายเป็นไทย
  // เพราะช่องไม่ว่างแล้ว (หน้าแก้ไขไม่มีค่าตั้งต้น จึงไม่มีอะไรให้ทับ)
  const untouchedDefaults = useRef(new Set(initialValue ? [] : Object.keys(DEFAULTS)))

  // รายการเขต/หลังหอพัก โหลดครั้งเดียวตอนเปิดฟอร์ม (37 เขต 866 หลัง)
  const [dorms, setDorms] = useState([])
  useEffect(() => {
    fetchDormitories().then(setDorms).catch(() => setDorms([]))
  }, [])

  // เพศตอนนี้เป็นค่าที่เราเดามาให้หรือยัง — ถ้าใช่ก็ทับได้เมื่อคำนำหน้าเปลี่ยน
  // พอครูกดเลือกเพศเองเมื่อไหร่ ธงนี้ลงถาวร เราไม่ยุ่งอีก
  // ในหน้าแก้ไข เพศที่โหลดมาจากฐานข้อมูลถือเป็นค่าที่คนกำหนดไว้แล้ว ไม่ใช่ค่าที่เราเดา
  const genderIsGuess = useRef(!initialValue?.student?.gender)

  const study = form.admission?.academicStudy || ''
  const sections = useMemo(() => sectionsFor(study), [study])

  // ปรับค่าที่พิมพ์ตามชนิดของช่อง ก่อนเก็บลงฟอร์ม
  const normaliseInput = (field, raw, prev) => {
    if (field.digits) return raw.replace(/\D/g, '')
    if (field.type === 'thaiDate') return thaiDateInput(raw)
    if (!field.namePrefix || manualNames.current.has(field.path)) return raw
    if (undidNamePrefix(prev, raw)) {
      manualNames.current.add(field.path)
      return raw
    }
    return splitNamePrefix(raw)
  }

  // เติมข้อมูลเดิมของนักเรียนเก่าลงเฉพาะช่องที่ยังว่าง — ครูอาจกรอกไปบ้างแล้วก่อนนึกได้
  // ว่าเป็นนักเรียนเก่า สิ่งที่พิมพ์เองไว้ต้องไม่หายไปเพราะเราไปดึงข้อมูลมาทับ
  useEffect(() => {
    if (!prefill) return
    setForm((prev) => {
      const next = { ...prev }
      for (const [group, values] of Object.entries(prefill)) {
        for (const [key, value] of Object.entries(values)) {
          if (value === '' || value == null) continue
          const path = `${group}.${key}`
          const empty = String(prev[group]?.[key] ?? '') === ''
          if (!empty && !untouchedDefaults.current.has(path)) continue
          untouchedDefaults.current.delete(path)
          next[group] = { ...next[group], [key]: value }
        }
      }
      return next
    })
    // เพศจากระเบียนเดิมเป็นข้อมูลจริง ไม่ใช่ค่าที่เราเดาจากคำนำหน้า
    if (prefill.student?.gender) genderIsGuess.current = false
  }, [prefill])

  // เขตหอพักที่เลือกได้ = เขตของเพศผู้สมัคร (เขตชายกับหญิงใช้เลขซ้ำกัน)
  const gender = form.student?.gender || ''
  const zones = useMemo(() => dorms.filter((v) => v.gender === gender), [dorms, gender])
  const zone = form.admission?.dormZone || ''
  const buildings = useMemo(
    () => zones.find((v) => v.zone === zone)?.buildings || [], [zones, zone])

  // ใช้ใน setValue ซึ่งเป็น useCallback([]) — อ่านผ่าน ref เพื่อไม่ต้องสร้างใหม่ทุกครั้งที่พิมพ์
  const dormsRef = useRef(dorms)
  dormsRef.current = dorms

  const setValue = useCallback((path, value) => {
    const [group, key] = path.split('.')

    untouchedDefaults.current.delete(path)
    if (path === 'student.gender') genderIsGuess.current = false
    const guessed = path === 'student.name' && genderIsGuess.current ? guessGender(value) : ''

    setForm((prev) => {
      const next = { ...prev, [group]: { ...prev[group], [key]: value } }

      // เปลี่ยนสายแล้วชั้นเดิมอาจไม่มีในสายใหม่ — ล้างทิ้งดีกว่าปล่อยให้ส่งค่าที่ server ปฏิเสธ
      if (path === 'admission.academicStudy') {
        const allowed = (CLASS_BY_STUDY[value] || []).map(([v]) => v)
        if (!allowed.includes(next.admission.academicClass)) {
          next.admission = { ...next.admission, academicClass: '' }
        }
      }

      // เติมเพศให้จากคำนำหน้าชื่อ
      if (guessed && guessed !== prev.student?.gender) {
        next.student = { ...next.student, gender: guessed }
      }

      // เปลี่ยนเพศแล้วเขตเดิมอาจเป็นเขตของอีกเพศ — ล้างทิ้งดีกว่าปล่อยให้บันทึกเขตผิดฝั่ง
      const nextGender = next.student?.gender || ''
      const zoneList = dormsRef.current.filter((v) => v.gender === nextGender)
      const zoneNow = next.admission?.dormZone || ''
      if (zoneNow && zoneList.length && !zoneList.some((v) => v.zone === zoneNow)) {
        next.admission = { ...next.admission, dormZone: '', dormNumber: '' }
      } else if (next.admission?.dormNumber) {
        // เปลี่ยนเขตแล้วเลขหลังเดิมอาจไม่มีในเขตใหม่
        const list = zoneList.find((v) => v.zone === (next.admission.dormZone || ''))?.buildings
        if (list && !list.some((b) => b.number === next.admission.dormNumber)) {
          next.admission = { ...next.admission, dormNumber: '' }
        }
      }
      return next
    })
    setErrors((prev) => (prev[path] ? { ...prev, [path]: undefined } : prev))
  }, [])

  // ตรวจเลขบัตรทันทีที่กรอกครบ 13 หลัก — บอกว่าซ้ำกับใคร ไม่ใช่แค่ว่าซ้ำ
  const verifyIdCard = useCallback(async (value) => {
    if (!/^\d{13}$/.test(value)) { setIdCardNote(null); return }
    setCheckingIdCard(true)
    try {
      const res = await checkIdCard(value)
      if (res.available) {
        setIdCardNote({ ok: true, text: 'เลขบัตรนี้ยังไม่มีในระบบ' })
      } else {
        const s = res.student
        setIdCardNote({
          ok: false,
          text: res.isApplicant
            ? `มีใบสมัครของเลขบัตรนี้แล้ว: ${s.st_idnumber} ${s.st_name} (${s.regis_academicClass || '-'})`
            : `เลขบัตรนี้เป็นของนักเรียนในระบบแล้ว: ${s.st_idnumber} ${s.st_name}`,
        })
      }
    } catch {
      setIdCardNote(null)
    } finally {
      setCheckingIdCard(false)
    }
  }, [])

  // คัดลอกที่อยู่ผู้สมัครไปให้บิดา/มารดา/ผู้ปกครอง — ส่วนใหญ่อยู่บ้านเดียวกัน
  // เดิมต้องพิมพ์ที่อยู่เดียวกันซ้ำสี่รอบต่อใบสมัครหนึ่งใบ
  const copyAddressTo = (group) => {
    const a = form.address || {}
    setForm((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        address: a.detail || '',
        subDistrict: a.subDistrict || '',
        district: a.district || '',
        province: a.province || '',
        zipcode: a.zipcode || '',
      },
    }))
  }

  const validate = () => {
    const found = {}
    for (const section of sections) {
      for (const field of section.fields) {
        if (!field.required) continue
        const [group, key] = field.path.split('.')
        if (!String(form[group]?.[key] ?? '').trim()) found[field.path] = 'จำเป็นต้องกรอก'
      }
    }
    const idcard = String(form.student?.idcard || '').trim()
    if (idcard && !/^\d{13}$/.test(idcard)) {
      found['student.idcard'] = 'ต้องเป็นตัวเลข 13 หลัก'
    }

    // วันที่ที่ server แปลงไม่ได้จะถูกเก็บเป็น null คือล้างวันเดิมทิ้ง ต้องกันไว้ตั้งแต่ที่นี่
    for (const section of sections) {
      for (const field of section.fields) {
        if (field.type !== 'thaiDate') continue
        const [group, key] = field.path.split('.')
        const value = String(form[group]?.[key] ?? '').trim()
        if (value && !isThaiDate(value)) found[field.path] = 'ต้องเป็น วว/ดด/ปปปป (พ.ศ.)'
      }
    }
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) {
      document.querySelector('.is-invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    onSubmit(formToPayload(form, study), form)
  }

  const renderField = (field) => {
    const [group, key] = field.path.split('.')
    const value = form[group]?.[key] ?? ''
    const invalid = Boolean(errors[field.path])
    const common = {
      id: field.path,
      value,
      isInvalid: invalid,
      disabled: submitting,
    }

    let control
    if (field.type === 'select') {
      control = (
        <Form.Select {...common} onChange={(e) => setValue(field.path, e.target.value)}>
          <option value="">— เลือก —</option>
          {field.options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          {orphan(value, field.options.map(([v]) => v), value)}
        </Form.Select>
      )
    } else if (field.type === 'classSelect') {
      const options = CLASS_BY_STUDY[study] || []
      control = (
        <Form.Select {...common} onChange={(e) => setValue(field.path, e.target.value)} disabled={submitting || !study}>
          <option value="">{study ? '— เลือก —' : 'เลือกสายก่อน'}</option>
          {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </Form.Select>
      )
    } else if (field.type === 'dormZone') {
      control = (
        <Form.Select {...common} onChange={(e) => setValue(field.path, e.target.value)}
                     disabled={submitting || !gender || !zones.length}>
          <option value="">{gender ? '— เลือกเขต —' : 'เลือกเพศผู้สมัครก่อน'}</option>
          {zones.map((v) => <option key={v.villageId} value={v.zone}>{v.name}</option>)}
          {orphan(value, zones.map((v) => v.zone), `เขต ${value}`)}
        </Form.Select>
      )
    } else if (field.type === 'dormBuilding') {
      control = (
        <Form.Select {...common} onChange={(e) => setValue(field.path, e.target.value)}
                     disabled={submitting || !buildings.length}>
          <option value="">{zone ? '— เลือกหลัง —' : 'เลือกเขตก่อน'}</option>
          {buildings.map((b) => <option key={b.name} value={b.number}>{b.name}</option>)}
          {orphan(value, buildings.map((b) => b.number), `${zone}/${value}`)}
        </Form.Select>
      )
    } else if (field.type === 'radio') {
      control = (
        <div className="pt-1">
          {field.options.map(([v, label]) => (
            <Form.Check
              key={v}
              inline
              type="radio"
              id={`${field.path}-${v}`}
              name={field.path}
              label={label}
              checked={value === v}
              disabled={submitting}
              onChange={() => setValue(field.path, v)}
            />
          ))}
        </div>
      )
    } else {
      control = (
        <Form.Control
          {...common}
          type={field.type === 'number' ? 'number' : 'text'}
          maxLength={field.type === 'thaiDate' ? 10 : field.maxLength}
          placeholder={field.type === 'thaiDate' ? 'วว/ดด/ปปปป (พ.ศ.)' : undefined}
          inputMode={field.digits || field.type === 'thaiDate' ? 'numeric' : undefined}
          onChange={(e) => setValue(field.path, normaliseInput(field, e.target.value, value))}
          onBlur={field.checkIdCard ? (e) => verifyIdCard(e.target.value) : undefined}
        />
      )
    }

    return (
      <div className={gridClass(field.col)} key={field.path}>
        <Form.Group className="mb-3">
          <Form.Label htmlFor={field.path} className="fs-sm mb-1">
            {field.label}
            {field.required && <span className="text-danger ms-1">*</span>}
          </Form.Label>
          {control}
          {invalid && <div className="invalid-feedback d-block">{errors[field.path]}</div>}
          {field.checkIdCard && checkingIdCard && (
            <div className="fs-xs text-secondary mt-1">
              <Spinner animation="border" size="sm" className="me-1" />กำลังตรวจสอบ…
            </div>
          )}
          {field.checkIdCard && !checkingIdCard && idCardNote && (
            <div className={`fs-xs mt-1 ${idCardNote.ok ? 'text-success' : 'text-danger'}`}>
              <i className={`ri-${idCardNote.ok ? 'checkbox-circle' : 'error-warning'}-line me-1`} />
              {idCardNote.text}
            </div>
          )}
        </Form.Group>
      </div>
    )
  }

  const canCopyAddress = (key) => ['father', 'mother', 'guardian'].includes(key)

  return (
    <Form onSubmit={handleSubmit} noValidate>
      {sections.map((section) => (
        <Card className="card-one mb-3" key={section.key}>
          <Card.Header className="d-flex align-items-center justify-content-between">
            <Card.Title as="h6" className="mb-0">
              <i className={`${section.icon} me-2`} />{section.title}
            </Card.Title>
            {canCopyAddress(section.key) && (
              <Button
                variant="outline-secondary"
                size="sm"
                type="button"
                disabled={submitting}
                onClick={() => copyAddressTo(section.key)}
              >
                <i className="ri-file-copy-line me-1" />ใช้ที่อยู่เดียวกับผู้สมัคร
              </Button>
            )}
          </Card.Header>
          <Card.Body>
            <Row>{section.fields.map(renderField)}</Row>
          </Card.Body>
        </Card>
      ))}

      {!study && (
        <div className="alert alert-info">
          <i className="ri-information-line me-1" />
          เลือก “สายที่สมัคร” ก่อน แล้วช่องกรอกที่เกี่ยวข้องกับระดับชั้นนั้นจะแสดงขึ้นมา
        </div>
      )}

      <div className="d-flex gap-2 justify-content-end mb-4">
        {onCancel && (
          <Button variant="secondary" type="button" onClick={onCancel} disabled={submitting}>
            ยกเลิก
          </Button>
        )}
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting && <Spinner animation="border" size="sm" className="me-2" />}
          {mode === 'edit' ? 'บันทึกการแก้ไข' : 'บันทึกใบสมัคร'}
        </Button>
      </div>
    </Form>
  )
}
